import React, { useContext, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import type { IconPrefix, IconName } from '@fortawesome/fontawesome-svg-core';
import styles from './style.module.scss';
import RamWidgetItem, { RamWidgetSkeleton } from '@components-clean/core/widgets/ram-widget-item';
import { CTQFactorsContext } from '@pages-clean/risk-assessment/interfaces/context';
import { CriticalToQualityFactorWidgetType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import { CtqWidgetValues, CtqWidgetValuesVariables } from '@queries/generated/risk-assessment.generated';
import { CTQ_WIDGET_VALUES } from '@queries/risk-assessment';

type WidgetType = CriticalToQualityFactorWidgetType;
type WidgetList = NonNullable<CtqWidgetValues['criticalToQualityFactorWidgetValues']>;
type WidgetItemGql = NonNullable<WidgetList[number]>;
type IconTuple = [IconPrefix, IconName];

const WIDGETS = [
    {
        type: CriticalToQualityFactorWidgetType.NUMBER_TOTAL,
        labelKey: 'numberTotal',
        icon: ['fas', 'file'] as IconTuple,
    },
    {
        type: CriticalToQualityFactorWidgetType.NUMBER_ACTIVE,
        labelKey: 'numberActive',
        icon: ['fad', 'file-circle-check'] as IconTuple,
    },
    {
        type: CriticalToQualityFactorWidgetType.NUMBER_ACTIVE_CRITICAL,
        labelKey: 'numberActiveCritical',
        icon: ['fad', 'file-circle-exclamation'] as IconTuple,
    },
    {
        type: CriticalToQualityFactorWidgetType.NUMBER_ACTIVE_CTQS_WITHOUT_RISKS,
        labelKey: 'numberActiveCtQsWithoutRisks',
        icon: ['fad', 'file-circle-minus'] as IconTuple,
    },
    {
        type: CriticalToQualityFactorWidgetType.NUMBER_ACTIVE_CTQS_WITHOUT_FUNCTIONAL_DATA_REVIEWS,
        labelKey: 'numberActiveCtQsWithoutFunctionalDataReviews',
        icon: ['fad', 'file-circle-xmark'] as IconTuple,
    },
] as const;

const ORDER: WidgetType[] = WIDGETS.map((w) => w.type);

const CONFIG = new Map<WidgetType, { labelKey: string; icon: IconTuple }>(
    WIDGETS.map((w) => [w.type, { labelKey: w.labelKey, icon: w.icon }]),
);

function orderAndFilter(raw: WidgetItemGql[]) {
    const byType = new Map<WidgetType, WidgetItemGql>(raw.map((w) => [w.type as WidgetType, w]));
    const result: WidgetItemGql[] = [];
    ORDER.forEach((t) => {
        const w = byType.get(t);
        if (w && w.show === true) result.push(w);
    });
    return result;
}

const WidgetsSection: React.FC = () => {
    const { t } = useTranslation('ram', { keyPrefix: 'ctqWidgets' });
    const ctx = useContext(CTQFactorsContext);
    const studyId = ctx?.current?.study?.id;

    const { data, loading, error } = useQuery<CtqWidgetValues, CtqWidgetValuesVariables>(CTQ_WIDGET_VALUES, {
        variables: studyId ? { studyId } : undefined,
        skip: !studyId,
        nextFetchPolicy: 'cache-first',
    });

    if (!studyId) return <div className={styles.container} />;

    const widgets = useMemo(
        () => orderAndFilter((data?.criticalToQualityFactorWidgetValues ?? []) as WidgetItemGql[]),
        [data],
    );

    if (loading) {
        return (
            <div className={styles.container}>
                <RamWidgetSkeleton />
                <RamWidgetSkeleton />
                <RamWidgetSkeleton />
                <RamWidgetSkeleton />
                <RamWidgetSkeleton />
            </div>
        );
    }

    if (error || !widgets.length) return <div className={styles.container} />;

    return (
        <div className={styles.container}>
            {widgets.map((item) => {
                const cfg = CONFIG.get(item.type as WidgetType);
                if (!cfg) return null;
                return (
                    <RamWidgetItem
                        key={item.type}
                        title={t(cfg.labelKey)}
                        value={item.value}
                        icon={cfg.icon}
                        data-testid={`ctq-widget-${cfg.labelKey}`}
                    />
                );
            })}
        </div>
    );
};

export default WidgetsSection;
